import React__default, { useState, useRef, useEffect } from 'react';
import { useFocusWithin } from 'react-aria';
import { Button, Autocomplete, MenuSection, Header } from 'react-aria-components';
import { TextInput } from '../atoms/TextInput.js';
import { useAsyncDebounce } from '../hooks/useAsyncDebounce.js';
import { useGlobalHotKey } from '../hooks/useGlobalHotKey.js';
import { SearchIcon } from '../icons/SearchIcon.js';
import { ActionMenu, ActionMenuItem } from './ActionMenu.js';
import './SearchInput.scss';

function SearchInput(props) {
    const isButton = !!props.onPress;
    const [internalIsLoading, setInternalIsLoading] = useState(false);
    // Handle popover with search results
    const [shouldDisplayPopover, setShouldDisplayPopover] = useState(false);
    const ref = useRef(null);
    const [focusedInput, setFocusedInput] = useState(false);
    const { focusWithinProps } = useFocusWithin({
        onFocusWithin: () => {
            setFocusedInput(true);
            if (props.showResultsOnFocus && props.handleSearch) {
                setShouldDisplayPopover(true);
                setInternalIsLoading(true);
                props.handleSearch('').then((results) => {
                    setItems(results ?? []);
                    setInternalIsLoading(false);
                });
            }
        },
        onBlurWithin: () => setFocusedInput(false),
    });
    const displayPopover = shouldDisplayPopover && focusedInput;
    useEffect(() => {
        if (!focusedInput) {
            ref.current?.blur();
        }
    }, [focusedInput]);
    useEffect(() => {
        props.onChangePopoverVisibility?.(displayPopover);
    }, [displayPopover, props]);
    //
    const handlePress = () => {
        if (!props.isDisabled && !props.isReadOnly && !props.isLoading) {
            props.onPress?.();
        }
    };
    useGlobalHotKey(props.hotKeysForOnPress, handlePress);
    const [_currentQuery, setCurrentQuery] = useState('');
    const [items, setItems] = useState([]);
    const { fn: fetchItems } = useAsyncDebounce(async (query) => {
        if (query.trim() || props.showResultsOnFocus) {
            const results = await props.handleSearch?.(query);
            setInternalIsLoading(false);
            setCurrentQuery(query);
            setItems(results ?? []);
            return results;
        }
        else {
            setInternalIsLoading(false);
            setCurrentQuery('');
            setItems([]);
            return [];
        }
    }, props.debounceDelay ?? 300);
    const InputComponent = (React__default.createElement(TextInput, { ...props, ref: ref, type: 'search', variant: props.variant ?? 'outline', leftContent: React__default.createElement(SearchIcon, null), isReadOnly: isButton || props.isReadOnly, isDisabled: props.isDisabled || props.isReadOnly, isButton: isButton, isLoading: props.isLoading || internalIsLoading, size: props.size ?? 'medium' }));
    return isButton ? (React__default.createElement(Button, { "data-rev-component": 'search-input', className: 'search-input-button--rev', onPress: handlePress, "data-rev-rounding": props.rounding ?? '10', isDisabled: props.isDisabled || props.isReadOnly }, InputComponent)) : (React__default.createElement("div", { "data-rev-component": 'search-input', ...focusWithinProps },
        React__default.createElement(Autocomplete, { onInputChange: async (query) => {
                setShouldDisplayPopover(props.showResultsOnFocus || !!query.trim());
                setFocusedInput(true);
                setInternalIsLoading(true);
                await fetchItems(query);
            } },
            InputComponent,
            displayPopover && (React__default.createElement("div", { className: 'search-input-autocomplete-menu--rev' },
                React__default.createElement(ActionMenu, { inPopover: false },
                    (items.length > 0 || internalIsLoading) && (React__default.createElement(MenuSection, null,
                        props.resultsHeaderContent && (React__default.createElement(Header, { "data-rev-component": 'action-menu-item', className: 'search-input-autocomplete-menu-header--rev' }, props.resultsHeaderContent)),
                        items.map((item) => (React__default.createElement(ActionMenuItem, { key: item.value, id: item.value, label: item.label, onAction: () => {
                                props.onSelectItem?.(item.value);
                                setFocusedInput(false);
                            } }))))),
                    items.length === 0 && !internalIsLoading && (React__default.createElement(MenuSection, null,
                        React__default.createElement(ActionMenuItem, { disabled: true, label: 'No results found' })))))))));
}

export { SearchInput };
//# sourceMappingURL=SearchInput.js.map
